'use client'
import Link from 'next/link'
import { motion } from 'framer-motion'
import TrendLogo from '@/components/ui/TrendLogo'
import { FadeInUp, Stagger, StaggerItem } from '@/components/ui/FadeInUp'
import {
  IconInstagram,
  IconTelegram,
  IconLinkedIn,
  IconMail,
  IconPhone,
  IconPin,
} from '@/components/ui/Icon'
import { useLanguage } from '@/lib/context/LanguageContext'
import { PHONE_E164 } from '@/lib/i18n'
import { toPersianNums } from '@/lib/persian'
import { GRADIENT_TEXT } from '@/lib/styles'

const socials = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/trendmarketingsolution?igsh=MXF5NnV5OWl3aGN6dg==',
    Icon: IconInstagram,
  },
  { label: 'Telegram', href: null, Icon: IconTelegram },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/trendu/posts/?feedView=all',
    Icon: IconLinkedIn,
  },
]

const focusRing =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2'

// Lift + dark-fill hover, shared by the social chips and the back-to-top button.
const chipHover = {
  whileHover: {
    y: -3,
    backgroundColor: '#0b1629',
    color: '#ffffff',
    borderColor: 'transparent',
  },
  whileTap: { scale: 0.94 },
  transition: { duration: 0.2 },
}

const eyebrow = 'text-xs font-bold uppercase tracking-[0.2em] text-muted/60'

export default function Footer() {
  const { t, isRTL } = useLanguage()

  // Footer nav: each label index in t.footer.links paired explicitly with its
  // destination so reordering the labels can't silently mis-link. Hrefs are
  // absolute (not bare hashes) because the footer also renders on blog pages;
  // FA keeps its language via the /fa homepage and /fa/blog.
  const home = isRTL ? '/fa' : '/'
  const footerNav = [
    { idx: 0, href: `${home}#services`, isPage: false },
    { idx: 2, href: `${home}#faq`, isPage: false },
    { idx: 4, href: isRTL ? '/fa/blog' : '/blog', isPage: true },
    { idx: 3, href: `${home}#contact`, isPage: false },
  ]

  const phone = t.contact.contactPhone
  const phoneLabel = isRTL ? toPersianNums(phone) : phone
  const telHref = `tel:${PHONE_E164}`

  return (
    <footer className="border-t border-ink/5 bg-tint">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* ── Tier 1 — CTA band ─────────────────────────────────────────── */}
        <FadeInUp>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 py-10 md:py-12 border-b border-ink/10">
            <div className="flex flex-col gap-1.5">
              <h2 className="font-display font-black text-2xl md:text-3xl text-ink leading-tight">
                {t.footer.ctaHeadline}
              </h2>
            </div>
            <motion.a
              href="#contact"
              className={`inline-flex items-center gap-2 self-start sm:self-auto px-6 py-3 rounded-full bg-ink text-white text-sm font-semibold whitespace-nowrap ${focusRing}`}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.2 }}
            >
              {t.footer.ctaButton}
              <span aria-hidden="true">{isRTL ? '←' : '→'}</span>
            </motion.a>
          </div>
        </FadeInUp>

        {/* ── Tier 2 — link grid ────────────────────────────────────────── */}
        <Stagger
          className="grid grid-cols-1 md:grid-cols-[1.8fr_1fr_1.4fr] gap-10 md:gap-12 py-10 md:py-14"
          staggerDelay={0.08}
        >
          {/* Brand */}
          <StaggerItem className="flex flex-col gap-4 max-w-sm">
            {/* Logo shares the tagline's exact alignment line (Hamed, after the
                edge experiment overflowed the viewport). self-start stops the
                flex column from stretching the img — stretched, the artwork
                letterboxes to the column center instead of the start line. The
                SVG viewBox is cropped to the artwork, so the visible mark —
                not a padded canvas — sits flush on that line. */}
            <TrendLogo size="sm" className="self-start" />
            <p className="text-sm text-muted leading-relaxed">{t.footer.tagline}</p>
            <div className="flex items-center gap-3 mt-1">
              {socials.map((s) =>
                s.href ? (
                  <motion.a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className={`w-9 h-9 rounded-full border border-ink/10 flex items-center justify-center text-muted ${focusRing}`}
                    {...chipHover}
                  >
                    <s.Icon className="w-4 h-4" />
                  </motion.a>
                ) : (
                  <motion.span
                    key={s.label}
                    role="img"
                    aria-label={s.label}
                    className={`w-9 h-9 rounded-full border border-ink/10 flex items-center justify-center text-muted cursor-default ${focusRing}`}
                    {...chipHover}
                  >
                    <s.Icon className="w-4 h-4" />
                  </motion.span>
                ),
              )}
            </div>
          </StaggerItem>

          {/* Nav links */}
          <StaggerItem className="flex flex-col gap-4">
            <p className={eyebrow}>{t.footer.navHeading}</p>
            <nav aria-label={t.footer.navHeading}>
              <ul className="flex flex-col gap-3">
                {footerNav.map(({ idx, href, isPage }) => (
                  <li key={href}>
                    {isPage ? (
                      <Link
                        href={href}
                        className={`text-sm text-muted hover:text-ink transition-colors ${focusRing}`}
                      >
                        {t.footer.links[idx]}
                      </Link>
                    ) : (
                      <a
                        href={href}
                        className={`text-sm text-muted hover:text-ink transition-colors ${focusRing}`}
                      >
                        {t.footer.links[idx]}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </StaggerItem>

          {/* Contact info */}
          <StaggerItem className="flex flex-col gap-4">
            <p className={eyebrow}>{t.footer.contactHeading}</p>
            <address className="flex flex-col gap-3 text-sm text-muted not-italic">
              <a
                href={`mailto:${t.contact.contactEmail}`}
                className={`flex items-center gap-2.5 hover:text-ink transition-colors ${focusRing}`}
              >
                <IconMail className="w-4 h-4 text-muted/70 shrink-0" aria-hidden="true" />
                {t.contact.contactEmail}
              </a>
              <a
                href={telHref}
                className={`flex items-center gap-2.5 hover:text-ink transition-colors ${focusRing}`}
              >
                <IconPhone className="w-4 h-4 text-muted/70 shrink-0" aria-hidden="true" />
                <span dir="ltr">{phoneLabel}</span>
              </a>
              <span className="flex items-start gap-2.5 leading-relaxed">
                <IconPin className="w-4 h-4 text-muted/70 shrink-0 mt-0.5" aria-hidden="true" />
                {t.contact.address}
              </span>
            </address>
          </StaggerItem>
        </Stagger>

        {/* ── Tier 3 — bottom bar ───────────────────────────────────────── */}
        <FadeInUp delay={0.1}>
          <div className="py-6 border-t border-ink/5 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-muted">
            <span>
              © {isRTL ? '۱۴۰۵' : String(new Date().getFullYear())}{' '}
              {isRTL ? 'شرکت ترندسازان خلاق' : 'Trend Creative Builders Co'}. {t.footer.rights}
            </span>
            <div className="flex items-center gap-5">
              <span className="font-medium" style={GRADIENT_TEXT}>
                {t.footer.sub}
              </span>
              <motion.button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label={t.footer.backToTop}
                className={`w-8 h-8 rounded-full border border-ink/10 flex items-center justify-center text-muted ${focusRing}`}
                {...chipHover}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </motion.button>
            </div>
          </div>
        </FadeInUp>
      </div>
    </footer>
  )
}
